/**
 * Scaled-integer wire types (ADR-001, ADR-010).
 *
 * JSON has one numeric type and it is a float. A `price_e8` such as
 * `108425000` survives that today, but `int64` money at scale does not survive
 * a JSON parser that turns it into a double. So every scaled integer travels
 * as a **canonical base-10 string** and is parsed to `bigint` at the edge.
 *
 * Canonical means: optional `-`, no leading zeros, no `+`, no exponent, no
 * decimal point. `"01"`, `"1.0"` and `"1e8"` are rejected rather than coerced —
 * coercion is how an amount silently changes.
 */

import { z } from 'zod';

import type { Money, Price, Rate, Volume } from '@weke/domain';

const CANONICAL_INT = /^(?:0|-?[1-9][0-9]*)$/;

/** int64 bounds (doc 03 §1.1). */
export const INT64_MIN = -(2n ** 63n);
export const INT64_MAX = 2n ** 63n - 1n;

/** Parse a canonical base-10 string to `bigint`, enforcing int64 range. */
export function parseInt64(text: string): bigint {
  if (!CANONICAL_INT.test(text)) {
    throw new RangeError(`not a canonical base-10 integer: ${JSON.stringify(text)}`);
  }
  const value = BigInt(text);
  if (value < INT64_MIN || value > INT64_MAX) {
    throw new RangeError(`outside int64 range: ${text}`);
  }
  return value;
}

/** Render a `bigint` as a canonical base-10 string, enforcing int64 range. */
export function formatInt64(value: bigint): string {
  if (value < INT64_MIN || value > INT64_MAX) {
    throw new RangeError(`outside int64 range: ${value.toString()}`);
  }
  return value.toString();
}

/** A canonical base-10 int64 string. The wire representation of any scaled int. */
export const Int64String = z
  .string()
  .regex(CANONICAL_INT, 'expected a canonical base-10 integer string')
  .refine(
    (text) => {
      const value = BigInt(text);
      return value >= INT64_MIN && value <= INT64_MAX;
    },
    { message: 'outside int64 range' },
  );

/** ISO-4217 code, upper case. */
export const CurrencyCode = z.string().regex(/^[A-Z]{3}$/, 'expected a 3-letter currency code');

/** Money on the wire: minor units plus the currency they belong to. */
export const MoneyWire = z.strictObject({
  minor: Int64String,
  ccy: CurrencyCode,
});
export type MoneyWire = z.infer<typeof MoneyWire>;

/** Price on the wire: `price_e8`. */
export const PriceWire = z.strictObject({ e8: Int64String });
export type PriceWire = z.infer<typeof PriceWire>;

/** Volume on the wire: `volume_e4` (lots × 1e4). */
export const VolumeWire = z.strictObject({ e4: Int64String });
export type VolumeWire = z.infer<typeof VolumeWire>;

/** Rate on the wire: `rate_e8`. */
export const RateWire = z.strictObject({ e8: Int64String });
export type RateWire = z.infer<typeof RateWire>;

export function encodeMoney(minor: Money, ccy: string): MoneyWire {
  return { minor: formatInt64(minor), ccy };
}

export function decodeMoney(wire: MoneyWire): { readonly minor: Money; readonly ccy: string } {
  return { minor: parseInt64(wire.minor) as Money, ccy: wire.ccy };
}

export function encodePrice(e8: Price): PriceWire {
  return { e8: formatInt64(e8) };
}

export function decodePrice(wire: PriceWire): Price {
  return parseInt64(wire.e8) as Price;
}

export function encodeVolume(e4: Volume): VolumeWire {
  return { e4: formatInt64(e4) };
}

export function decodeVolume(wire: VolumeWire): Volume {
  return parseInt64(wire.e4) as Volume;
}

export function encodeRate(e8: Rate): RateWire {
  return { e8: formatInt64(e8) };
}

export function decodeRate(wire: RateWire): Rate {
  return parseInt64(wire.e8) as Rate;
}
